import React, {Dispatch, useEffect, useMemo, useReducer, useState} from 'react';
import {Link, useHistory, useParams} from "react-router-dom";
import {AppContainer} from "../domain/AppContainer";
import './LeagueEvents.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus} from "@fortawesome/free-solid-svg-icons/faPlus";
import {Action, initialState, reducer} from "../domain/leagueevent/reducer";
import {GroupPlayer, isStaleEvent, LeagueEvent, LeagueEventsState} from "../domain/leagueevent/models";
import {CreateLeagueEvent} from "../components/CreateLeagueEvent";
import {GroupSetup} from "../components/GroupSetup";
import {RecordResults} from "../components/RecordResults";
import {ReviewEvent} from "../components/ReviewEvent";
import {useActiveEntity, useEntityStore, useObservable} from "../hooks/useEntityStore";
import {eventToLi, LeagueEventsList, LeagueEventsListItem} from "../component-library/LeagueEventsList";
import {Snackbar} from "../component-library/Snackbar";
import {FieldsetInput} from "../component-library/Fieldsets";
import {WorkPane} from "../component-library/WorkPane";

interface Props {
    container: AppContainer
}

export function LeagueEvents({container}: Props) {
    // this needs to move down a level so initialState can be passed to it
    const [, dispatch] = useReducer(reducer, initialState)
    const {leagueId, eventId} = useParams<{leagueId: string, eventId?: string}>()
    const history = useHistory()
    const active = useActiveEntity(container.leagueEvent.query.active$)
    const events = useEntityStore<LeagueEventsState>(container.leagueEvent.query.events$)
    const groups = useObservable<{ [groupNumber: number]: GroupPlayer[] }>(container.leagueEvent.query.groups$)
    const allowNextKeys = groups ? Object.keys(groups) : []
    const allowNext = allowNextKeys.length > 0 && allowNextKeys[0] !== '0'
    const [confirmingCancel, setConfirmingCancel] = useState(false)
    useEffect(() => {
        container.leagueEvent.service.getAllForLeague()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!events || events.length === 0) return
        if (eventId) {
            if (String(active?.id) === eventId) return
            const match = events.find(e => String(e.id) === eventId)
            if (match) {
                container.leagueEvent.service.selectLeagueEvent(match)
            } else {
                const first = events[0]
                container.leagueEvent.service.selectLeagueEvent(first)
                history.replace(`/league/${leagueId}/manage/events/${first.id}`)
            }
        } else {
            const first = events[0]
            container.leagueEvent.service.selectLeagueEvent(first)
            history.replace(`/league/${leagueId}/manage/events/${first.id}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, events, active])

    useEffect(() => {
        setConfirmingCancel(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.id, active?.status])

    const {activeEvents, staleEvents} = useMemo(() => {
        const active: LeagueEventsListItem[] = []
        const stale: LeagueEventsListItem[] = []
        events.forEach(e => {
            if (e.status === 'done') return
            ;(isStaleEvent(e) ? stale : active).push(eventToLi(e))
        })
        return {activeEvents: active, staleEvents: stale}
    }, [events]);

    return <div className="league-events">
        <section className="league-events-left">
            <button
                className="button"
                onClick={() => {
                    container.leagueEvent.service.clear()
                    container.leagueEvent.service.createLeagueEvent()
                }}>
                <FontAwesomeIcon icon={faPlus}/> New Event
            </button>
            <LeagueEventsList
                title={"Active Events"}
                leagueEvents={activeEvents}
                activeId={active?.id}
                onClick={id => history.push(`/league/${leagueId}/manage/events/${id}`)}/>

            <LeagueEventsList
                title={"Stale"}
                leagueEvents={staleEvents}
                dimmed={true}
                activeId={active?.id}
                onClick={id => history.push(`/league/${leagueId}/manage/events/${id}`)}/>

            <Link
                className="league-events-archive-link"
                to={`/league/${leagueId}/events`}>
                View public results page →
            </Link>
        </section>
        <section className="league-events-main">
            {events && events.length === 0 && (
                <Snackbar level={"info"} showIcon={true}>
                    Creating an event allows you to quickly record the results of a round robin tournament.
                    <p>Click <button className="link-button"
                                     onClick={() => container.leagueEvent.service.createLeagueEvent()}>New
                        Event</button> to get started.</p>

                </Snackbar>
            )}
            {active && (
                <div className="league-events-main-body">
                    {active.status !== 'done' && (
                        <WorkPane sticky={true} className="event-info">
                            <h3>Round Robin Event</h3>
                            <main className="event-info-body">
                                <div className="event-info-row">
                                    <FieldsetInput
                                        label={"Name"}
                                        id={"event-name"}
                                        value={active.name}
                                        onChange={(v) => container.leagueEvent.service.updateName(v)}/>
                                    <FieldsetInput
                                        type={"date"}
                                        label={"Date"}
                                        id={"event-date"}
                                        value={active.dateString}
                                        onChange={(v) => container.leagueEvent.service.updateDateString(v)}/>
                                </div>
                                <div className="event-info-actions">
                                    {confirmingCancel ? (
                                        <div className="event-info-confirm-row">
                                            <span>Cancel this event?</span>
                                            <button
                                                className="button event-info-cancel-danger"
                                                onClick={() => {
                                                    setConfirmingCancel(false)
                                                    container.leagueEvent.service.deleteEvent(active.id)
                                                    history.replace(`/league/${leagueId}/manage/events`)
                                                }}>
                                                Cancel the event
                                            </button>
                                            <button
                                                className="link-button event-info-keep"
                                                onClick={() => setConfirmingCancel(false)}>
                                                Keep event
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {active.status === 'not-started' && (
                                                <input
                                                    type="submit"
                                                    className="button"
                                                    disabled={!allowNext}
                                                    value={allowNext ? 'Record Results' : 'Create a group to proceed'}
                                                    onClick={() => container.leagueEvent.service.setStatus("recording")}/>
                                            )}
                                            {(active.status === 'setting-up' || active.status === 'in-progress') && (
                                                <button
                                                    className="button"
                                                    onClick={() => container.leagueEvent.service.setStatus("recording")}>
                                                    Start
                                                </button>
                                            )}
                                            {active.status === 'recording' && (
                                                <input
                                                    type="submit"
                                                    className="button"
                                                    value="Finish"
                                                    onClick={() => container.leagueEvent.service.setStatus("done")}/>
                                            )}
                                            <button
                                                className="link-button event-info-cancel-btn"
                                                aria-label={`Cancel event ${active.name}`}
                                                onClick={() => setConfirmingCancel(true)}>
                                                Cancel event
                                            </button>
                                        </>
                                    )}
                                </div>
                            </main>
                        </WorkPane>
                    )}
                    <LeagueEventBody
                        container={container}
                        event={active}
                        dispatch={dispatch}/>
                </div>
            )}
        </section>
    </div>
}

interface LeagueEventBodyProps {
    container: AppContainer
    event: LeagueEvent
    dispatch: Dispatch<Action<any>>
}

function LeagueEventBody({container, event}: LeagueEventBodyProps) {
    if (!event) {
        return null;
    }

    switch (event.status) {
        case "not-started":
            return <CreateLeagueEvent container={container} event={event}/>
        case "setting-up":
        case "in-progress":
            return <GroupSetup container={container} event={event}/>
        case "recording":
            return <RecordResults container={container} event={event}/>
        case "done":
            return <ReviewEvent container={container} event={event}/>
        default:
            throw new Error('state not implemented')
    }
}